import { useState, useEffect, useRef } from 'react'
import type { WorkshopBlock } from '@/types'
import { sanitizeHtml } from '@/lib/sanitize'

// ============================================
// Helper: reemplaza Math.random() con crypto seguro (client-side)
// ============================================
function secureRandom(): number {
  const array = new Uint32Array(1)
  window.crypto.getRandomValues(array)
  return array[0] / (0xFFFFFFFF + 1)
}

export default function WorkshopBlockRenderer({ block }: { block: WorkshopBlock }) {
  const config = block.config as any
  const renderers: Record<string, () => React.JSX.Element> = {
    title_slide: () => <TitleSlide config={config} />,
    vocabulary: () => <Vocabulary config={config} />,
    phrase_list: () => <PhraseList config={config} />,
    dialogue: () => <Dialogue config={config} />,
    roulette: () => <Roulette config={config} />,
    flashcards: () => <Flashcards config={config} />,
    matching: () => <Matching config={config} />,
    fill_blanks: () => <FillBlanks config={config} />,
    role_play: () => <RolePlay config={config} />,
    image_describe: () => <ImageDescribe config={config} />,
    poll: () => <Poll config={config} />,
    timer_activity: () => <TimerActivity config={config} />,
    free_html: () => <FreeHtml config={config} />,
    video_embed: () => <VideoEmbed config={config} />,
    audio_listen: () => <AudioListen config={config} />,
    sentence_builder: () => <SentenceBuilder config={config} />,
    conversation_prompts: () => <ConversationPrompts config={config} />,
  }
  const render = renderers[block.block_type]
  if (!render) return <div className="text-center text-gray-400 py-8"><p>Tipo no soportado: <code>{block.block_type}</code></p></div>

  return (
    <div>
      {block.title && block.block_type !== 'title_slide' && (
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 mb-3 text-center tracking-tight">{block.title}</h2>
      )}
      {render()}
    </div>
  )
}

// ============================================
// 1. TITLE SLIDE
// ============================================
function TitleSlide({ config }: { config: any }) {
  const bg = config.background_color || '#4f46e5'
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] rounded-3xl p-10 text-center shadow-2xl relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${bg}, ${bg}dd)` }}>
      <div className="absolute top-0 right-0 w-52 h-52 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
      {config.image_url && <img src={config.image_url} alt="" className="max-h-20 mb-4 rounded-xl shadow-lg relative z-10" />}
      <h1 className="text-5xl sm:text-6xl font-black text-white mb-3 relative z-10 drop-shadow-lg">{config.heading || 'Bienvenue!'}</h1>
      {config.subheading && <p className="text-xl sm:text-2xl text-white/90 font-medium relative z-10">{config.subheading}</p>}
      {config.show_date && <p className="text-white/60 mt-4 text-base relative z-10">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>}
    </div>
  )
}

// ============================================
// 2. VOCABULARY
// ============================================
function Vocabulary({ config }: { config: any }) {
  const words = config.words || []
  // Auto-calculate columns based on word count
  const cols: number = words.length <= 4 ? 2 : words.length <= 6 ? 2 : 3
  const colClass = cols === 1 ? 'grid-cols-1' : cols === 3 ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
  const isCompact = words.length > 6

  return (
    <div>
      {config.title && (
        <div className="text-center mb-3">
          <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-full font-bold shadow-md">{config.title}</span>
        </div>
      )}
      <div className={`grid gap-2.5 ${colClass}`}>
        {words.map((w: any, i: number) => (
          <div key={i} className="bg-white rounded-xl p-3 border-2 border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all flex items-center gap-3">
            {w.image_url && <img src={w.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className={`${isCompact ? 'text-lg' : 'text-xl'} font-black text-gray-800`}>{w.fr}</span>
                {config.show_phonetic && w.phonetic && <span className="text-xs text-purple-500 font-mono font-bold">/{w.phonetic}/</span>}
              </div>
              <p className="text-gray-500 text-sm">{w.es}</p>
              {!isCompact && w.example && <p className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded mt-1 italic truncate">"{w.example}"</p>}
            </div>
            {w.audio_url && <button onClick={() => new Audio(w.audio_url).play()} className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 text-sm flex-shrink-0">🔊</button>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================
// 3. PHRASE LIST
// ============================================
function PhraseList({ config }: { config: any }) {
  const categories = config.categories || []
  const categoryColors = [
    { bg: 'bg-blue-50', border: 'border-blue-200', title: 'text-blue-700' },
    { bg: 'bg-rose-50', border: 'border-rose-200', title: 'text-rose-700' },
    { bg: 'bg-amber-50', border: 'border-amber-200', title: 'text-amber-700' },
    { bg: 'bg-emerald-50', border: 'border-emerald-200', title: 'text-emerald-700' },
  ]
  const multiCategory = categories.length > 1

  return (
    <div className={multiCategory ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
      {categories.map((cat: any, ci: number) => {
        const color = categoryColors[ci % categoryColors.length]
        return (
          <div key={ci} className={`${color.bg} rounded-xl p-3 border-2 ${color.border}`}>
            {cat.name && <h3 className={`text-sm font-bold ${color.title} mb-2`}>{cat.name}</h3>}
            <div className="space-y-1">
              {(cat.phrases || []).map((p: any, pi: number) => (
                <div key={pi} className="bg-white rounded-lg px-3 py-2 shadow-sm flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-gray-800 truncate">{p.fr}</p>
                    <p className="text-gray-500 text-xs truncate">{p.es}</p>
                  </div>
                  {p.phonetic && <p className="text-xs text-purple-500 font-mono font-semibold hidden sm:block flex-shrink-0">/{p.phonetic}/</p>}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ============================================
// 4. DIALOGUE
// ============================================
function Dialogue({ config }: { config: any }) {
  const [showTranslation, setShowTranslation] = useState(config.show_translation ?? true)
  const characters = config.characters || []
  const lines = config.lines || []
  const getColor = (name: string) => characters.find((c: any) => c.name === name)?.color || '#6366f1'
  const isCompact = lines.length > 5

  return (
    <div className="max-w-4xl mx-auto">
      {config.title && (
        <div className="text-center mb-2">
          <span className="inline-block px-4 py-1 bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded-full font-bold shadow-md text-sm">{config.title}</span>
        </div>
      )}
      <div className="flex justify-center mb-3 gap-2">
        {characters.map((c: any, i: number) => (
          <span key={i} className="px-3 py-1 rounded-full text-white font-bold text-xs shadow" style={{ backgroundColor: c.color }}>{c.name}</span>
        ))}
        <button onClick={() => setShowTranslation(!showTranslation)} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-gray-200">
          {showTranslation ? '🙈 Ocultar' : '👁 Traducción'}
        </button>
      </div>
      <div className={`${isCompact ? 'grid grid-cols-2 gap-x-4 gap-y-1.5' : 'space-y-2'}`}>
        {lines.map((line: any, i: number) => {
          const color = getColor(line.character)
          const isLeft = characters.findIndex((c: any) => c.name === line.character) % 2 === 0
          return (
            <div key={i} className={`flex items-start gap-2 ${!isCompact && !isLeft ? 'flex-row-reverse' : ''}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0 shadow" style={{ backgroundColor: color }}>
                {line.character?.[0]}
              </div>
              <div className={`flex-1 rounded-xl px-3 py-2 shadow-sm ${!isCompact && !isLeft ? 'rounded-tr-sm' : 'rounded-tl-sm'}`} style={{ backgroundColor: `${color}12`, borderLeft: `3px solid ${color}` }}>
                <p className="text-sm font-semibold text-gray-800">{line.fr}</p>
                {showTranslation && line.es && <p className="text-gray-500 text-xs">{line.es}</p>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// 5. ROULETTE
// ============================================
function Roulette({ config }: { config: any }) {
  const items = config.items || []
  const colors = config.colors || ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899']
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [rotation, setRotation] = useState(0)

  const spin = () => {
    if (spinning || items.length === 0) return
    setSpinning(true); setResult(null)
    const extraSpins = 5 + secureRandom() * 5
    const segmentAngle = 360 / items.length
    const winnerIndex = Math.floor(secureRandom() * items.length)
    const targetRotation = rotation + (extraSpins * 360) + (360 - winnerIndex * segmentAngle - segmentAngle / 2)
    setRotation(targetRotation)
    setTimeout(() => { setResult(items[winnerIndex]); setSpinning(false) }, config.spin_duration_ms || 4000)
  }

  return (
    <div className="flex items-center gap-8 max-w-5xl mx-auto">
      <div className="flex-1 flex flex-col items-center">
        {config.subtitle && <p className="text-sm text-gray-500 mb-3">{config.subtitle}</p>}
        <div className="relative w-60 h-60 sm:w-72 sm:h-72">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 text-3xl drop-shadow-lg">🔻</div>
          <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-xl" style={{ transform: `rotate(${rotation}deg)`, transition: spinning ? `transform ${(config.spin_duration_ms || 4000) / 1000}s cubic-bezier(0.17, 0.67, 0.12, 0.99)` : 'none' }}>
            {items.map((item: string, i: number) => {
              const angle = 360 / items.length
              const startAngle = i * angle - 90, endAngle = startAngle + angle
              const startRad = (startAngle * Math.PI) / 180, endRad = (endAngle * Math.PI) / 180
              const x1 = 100 + 95 * Math.cos(startRad), y1 = 100 + 95 * Math.sin(startRad)
              const x2 = 100 + 95 * Math.cos(endRad), y2 = 100 + 95 * Math.sin(endRad)
              const midRad = ((startAngle + angle / 2) * Math.PI) / 180
              const textX = 100 + 60 * Math.cos(midRad), textY = 100 + 60 * Math.sin(midRad)
              return (
                <g key={i}>
                  <path d={`M100,100 L${x1},${y1} A95,95 0 ${angle > 180 ? 1 : 0},1 ${x2},${y2} Z`} fill={colors[i % colors.length]} stroke="white" strokeWidth="2" />
                  <text x={textX} y={textY} fill="white" fontSize="5" fontWeight="bold" textAnchor="middle" dominantBaseline="middle" transform={`rotate(${startAngle + angle / 2 + 90}, ${textX}, ${textY})`}>
                    {item.length > 20 ? item.slice(0, 20) + '…' : item}
                  </text>
                </g>
              )
            })}
            <circle cx="100" cy="100" r="16" fill="white" /><circle cx="100" cy="100" r="12" fill="#6366f1" />
          </svg>
        </div>
        <button onClick={spin} disabled={spinning} className={`mt-4 px-8 py-3 text-white rounded-2xl text-lg font-black shadow-xl transition-all ${spinning ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:scale-105 active:scale-95'}`}>
          {spinning ? '🎯 Girando...' : '🎰 ¡GIRAR!'}
        </button>
      </div>
      <div className="flex-1">
        {result ? (
          <div className="p-6 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-amber-300 rounded-2xl shadow-lg animate-fadeIn text-center">
            <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">Resultado</p>
            <p className="text-2xl font-black text-gray-800">{result}</p>
          </div>
        ) : (
          <div className="p-6 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl text-center">
            <p className="text-gray-400 text-lg">Gira la ruleta para ver el resultado</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// 6. FLASHCARDS
// ============================================
function Flashcards({ config }: { config: any }) {
  const rawCards = config.cards || []
  const [cards, setCards] = useState(rawCards)
  const [flipped, setFlipped] = useState<Set<number>>(new Set())

  useEffect(() => {
    setCards(config.shuffle ? [...rawCards].sort(() => secureRandom() - 0.5) : rawCards)
    setFlipped(new Set())
  }, [config])

  const toggleFlip = (i: number) => setFlipped(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n })
  const cardColors = ['from-blue-400 to-blue-600', 'from-rose-400 to-rose-600', 'from-emerald-400 to-emerald-600', 'from-amber-400 to-amber-600', 'from-purple-400 to-purple-600', 'from-cyan-400 to-cyan-600', 'from-pink-400 to-pink-600', 'from-teal-400 to-teal-600', 'from-indigo-400 to-indigo-600', 'from-orange-400 to-orange-600', 'from-lime-400 to-lime-600', 'from-fuchsia-400 to-fuchsia-600']
  const isStack = config.layout === 'stack'
  const cols = isStack ? 'max-w-md mx-auto space-y-2' : cards.length <= 6 ? 'grid grid-cols-2 sm:grid-cols-3 gap-2.5' : 'grid grid-cols-3 sm:grid-cols-4 gap-2'

  return (
    <div className={cols}>
      {cards.map((card: any, i: number) => {
        const isFlipped = flipped.has(i)
        return (
          <div
            key={i}
            role="button"
            tabIndex={0}
            onClick={() => toggleFlip(i)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleFlip(i) }}
            className="cursor-pointer group"
          >
            <div className={`rounded-xl p-4 min-h-[80px] flex flex-col items-center justify-center text-center shadow-md transition-all duration-200 ${
              isFlipped ? 'bg-white border-2 border-green-400' : `bg-gradient-to-br ${cardColors[i % cardColors.length]} hover:scale-[1.03]`
            }`}>
              {isFlipped ? (
                <>
                  <p className="text-lg font-black text-green-600">{card.back}</p>
                  {config.show_hint && card.hint && <p className="text-xs text-gray-400 mt-1">{card.hint}</p>}
                </>
              ) : (
                <p className="text-lg font-black text-white drop-shadow-sm">{card.front}</p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ============================================
// 7. MATCHING
// ============================================
function Matching({ config }: { config: any }) {
  const pairs = config.pairs || []
  const [selected, setSelected] = useState<{ side: 'left' | 'right'; index: number } | null>(null)
  const [matched, setMatched] = useState<Set<number>>(new Set())
  const [wrong, setWrong] = useState<{ side: 'left' | 'right'; index: number } | null>(null)
  const [shuffledRight, setShuffledRight] = useState<number[]>([])

  useEffect(() => {
    const indices = pairs.map((_: any, i: number) => i)
    setShuffledRight(config.shuffle_sides ? indices.sort(() => secureRandom() - 0.5) : indices)
    setMatched(new Set()); setSelected(null); setWrong(null)
  }, [config])

  const handleClick = (side: 'left' | 'right', pairIndex: number) => {
    if (matched.has(pairIndex)) return; setWrong(null)
    if (!selected) { setSelected({ side, index: pairIndex }); return }
    if (selected.side === side) { setSelected({ side, index: pairIndex }); return }
    if (selected.index === pairIndex) { setMatched(prev => new Set(prev).add(pairIndex)); setSelected(null) }
    else { setWrong({ side, index: pairIndex }); setTimeout(() => { setWrong(null); setSelected(null) }, 500) }
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-6 max-w-3xl mx-auto">
        <div>
          {config.left_label && <p className="text-center mb-2"><span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">{config.left_label}</span></p>}
          <div className="space-y-1.5">
            {pairs.map((pair: any, i: number) => (
              <button key={i} onClick={() => handleClick('left', i)} disabled={matched.has(i)}
                className={`w-full px-4 py-2.5 rounded-xl text-left font-bold transition-all border-2 text-sm ${
                  matched.has(i) ? 'bg-green-50 text-green-700 border-green-400' :
                  selected?.side === 'left' && selected.index === i ? 'bg-blue-500 text-white border-blue-500 shadow-lg scale-[1.02]' :
                  'bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:shadow-md shadow-sm'
                }`}>{matched.has(i) && '✅ '}{pair.left}</button>
            ))}
          </div>
        </div>
        <div>
          {config.right_label && <p className="text-center mb-2"><span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">{config.right_label}</span></p>}
          <div className="space-y-1.5">
            {shuffledRight.map((pairIndex: number) => (
              <button key={pairIndex} onClick={() => handleClick('right', pairIndex)} disabled={matched.has(pairIndex)}
                className={`w-full px-4 py-2.5 rounded-xl text-left font-bold transition-all border-2 text-sm ${
                  matched.has(pairIndex) ? 'bg-green-50 text-green-700 border-green-400' :
                  wrong?.side === 'right' && wrong.index === pairIndex ? 'bg-red-100 text-red-600 border-red-400 animate-shake' :
                  selected?.side === 'right' && selected.index === pairIndex ? 'bg-orange-500 text-white border-orange-500 shadow-lg scale-[1.02]' :
                  'bg-white text-gray-700 border-gray-200 hover:border-orange-400 hover:shadow-md shadow-sm'
                }`}>{matched.has(pairIndex) && '✅ '}{pairs[pairIndex].right}</button>
            ))}
          </div>
        </div>
      </div>
      {matched.size === pairs.length && (
        <div className="text-center mt-4 animate-fadeIn">
          <span className="text-3xl">🎉</span>
          <p className="text-xl font-black text-green-600">Très bien !</p>
        </div>
      )}
    </div>
  )
}

// ============================================
// 8. FILL BLANKS
// ============================================
function FillBlanks({ config }: { config: any }) {
  const sentences = config.sentences || []
  const [revealed, setRevealed] = useState<Set<number>>(new Set())
  const handleReveal = (i: number) => {
    if (config.reveal_mode === 'all_at_once') setRevealed(new Set(sentences.map((_: any, idx: number) => idx)))
    else setRevealed(prev => new Set(prev).add(i))
  }
  const isCompact = sentences.length > 5

  return (
    <div className={`${isCompact ? 'grid grid-cols-2 gap-2.5' : 'space-y-2.5'} max-w-4xl mx-auto`}>
      {sentences.map((s: any, i: number) => {
        const parts = s.text.split('___')
        const isRevealed = revealed.has(i)
        return (
          <div key={i} className={`bg-white rounded-xl p-4 shadow-sm border-2 transition-all ${isRevealed ? 'border-green-300 bg-green-50/30' : 'border-gray-100'}`}>
            <p className="text-lg text-gray-800">
              {parts.map((part: string, pi: number) => (
                <span key={pi}>
                  {part}
                  {pi < parts.length - 1 && (
                    <button onClick={() => handleReveal(i)} className={`inline-block mx-1 px-3 py-0.5 rounded-lg font-black transition-all ${
                      isRevealed ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-yellow-200 cursor-pointer animate-pulse'
                    }`}>{isRevealed ? s.answer : '???'}</button>
                  )}
                </span>
              ))}
            </p>
            {config.show_hints && s.hint && !isRevealed && <p className="text-xs text-purple-500 mt-1">💡 {s.hint}</p>}
          </div>
        )
      })}
    </div>
  )
}

// ============================================
// 9. ROLE PLAY
// ============================================
function RolePlay({ config }: { config: any }) {
  const roles = config.roles || []
  const roleColors = [
    { gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', border: 'border-blue-200' },
    { gradient: 'from-rose-500 to-pink-500', bg: 'bg-rose-50', border: 'border-rose-200' },
  ]
  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-5 text-center shadow-xl flex items-center justify-center gap-4">
        <span className="text-4xl">🎭</span>
        <div>
          <p className="text-lg text-white font-bold">{config.scenario}</p>
          {config.scenario_en && <p className="text-white/80 italic text-sm">{config.scenario_en}</p>}
        </div>
        {config.duration_minutes && <span className="px-3 py-1 bg-white/20 text-white rounded-full font-bold text-sm flex-shrink-0">⏱ {config.duration_minutes}min</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {roles.map((role: any, i: number) => {
          const color = roleColors[i % roleColors.length]
          return (
            <div key={i} className={`${color.bg} rounded-xl p-4 border-2 ${color.border}`}>
              <h4 className={`text-lg font-black bg-gradient-to-r ${color.gradient} bg-clip-text text-transparent mb-1`}>{role.name}</h4>
              <p className="text-gray-600 text-sm mb-2">{role.description}</p>
              {role.suggested_phrases?.length > 0 && (
                <div className="space-y-1">
                  {role.suggested_phrases.map((phrase: string, pi: number) => (
                    <p key={pi} className="text-gray-700 bg-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm">💬 {phrase}</p>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {config.tips && <div className="bg-amber-50 border-2 border-amber-200 rounded-xl px-4 py-2 text-center"><p className="text-amber-700 font-medium text-sm">💡 {config.tips}</p></div>}
    </div>
  )
}

// ============================================
// 10. IMAGE DESCRIBE
// ============================================
function ImageDescribe({ config }: { config: any }) {
  return (
    <div className="flex gap-5 max-w-5xl mx-auto items-start">
      {config.image_url && (
        <div className="flex-1 rounded-2xl overflow-hidden shadow-xl border-2 border-gray-100">
          <img src={config.image_url} alt={config.caption || ''} className="w-full max-h-[55vh] object-contain bg-gray-50" />
          {config.caption && <p className="text-center text-gray-500 italic text-sm py-2 bg-white">{config.caption}</p>}
        </div>
      )}
      <div className="w-80 flex-shrink-0 space-y-3">
        {config.questions?.length > 0 && (
          <div className="bg-indigo-50 rounded-xl p-4 border-2 border-indigo-200">
            <p className="font-bold text-indigo-700 mb-2 text-sm">🗣 Discutir:</p>
            <div className="space-y-1.5">
              {config.questions.map((q: string, i: number) => (
                <p key={i} className="text-gray-700 bg-white px-3 py-2 rounded-lg shadow-sm text-sm"><span className="font-bold text-indigo-500">{i + 1}.</span> {q}</p>
              ))}
            </div>
          </div>
        )}
        {config.vocabulary_help?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {config.vocabulary_help.map((word: string, i: number) => (
              <span key={i} className="px-3 py-1 bg-white text-indigo-600 rounded-full text-xs font-bold border border-indigo-200 shadow-sm">{word}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================
// 11. POLL
// ============================================
function Poll({ config }: { config: any }) {
  const options = config.options || []
  const [showAnswer, setShowAnswer] = useState(false)
  const optionColors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-purple-500', 'bg-pink-500']

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-6 text-center shadow-xl">
        <p className="text-xl sm:text-2xl font-black text-white">{config.question}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {options.map((opt: any, i: number) => (
          <div key={i} className={`flex items-center gap-3 px-5 py-4 rounded-xl border-2 transition-all shadow-sm ${
            showAnswer && opt.is_correct ? 'bg-green-50 border-green-400 ring-2 ring-green-200 scale-[1.02]' :
            showAnswer && !opt.is_correct ? 'bg-gray-50 border-gray-200 opacity-50' :
            'bg-white border-gray-100'
          }`}>
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-black ${showAnswer && opt.is_correct ? 'bg-green-500' : optionColors[i % optionColors.length]}`}>
              {showAnswer && opt.is_correct ? '✓' : String.fromCodePoint(65 + i)}
            </span>
            <span className="text-lg font-bold text-gray-700 flex-1">{opt.text}</span>
            {showAnswer && opt.is_correct && <span className="text-2xl">✅</span>}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-4">
        <button onClick={() => setShowAnswer(!showAnswer)} className={`px-6 py-2.5 rounded-xl font-black shadow-lg transition-all hover:scale-105 ${
          showAnswer ? 'bg-gray-200 text-gray-700' : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white'
        }`}>{showAnswer ? '🔒 Ocultar' : '🔓 Revelar'}</button>
      </div>
      {showAnswer && config.explanation && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 text-center animate-fadeIn">
          <p className="text-purple-700 font-medium text-sm">💡 {config.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ============================================
// 12. TIMER ACTIVITY
// ============================================
function TimerActivity({ config }: { config: any }) {
  const totalSeconds = config.duration_seconds || 180
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds)
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const intervalRef = useRef<number | null>(null)

  const start = () => { setRunning(true); setFinished(false); if (secondsLeft === 0) setSecondsLeft(totalSeconds) }
  const pause = () => setRunning(false)
  const reset = () => { setRunning(false); setFinished(false); setSecondsLeft(totalSeconds) }

  useEffect(() => {
    if (running && secondsLeft > 0) {
      intervalRef.current = window.setInterval(() => {
        setSecondsLeft((prev: number) => { if (prev <= 1) { setRunning(false); setFinished(true); return 0 }; return prev - 1 })
      }, 1000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [running])

  const min = Math.floor(secondsLeft / 60), sec = secondsLeft % 60
  const progress = ((totalSeconds - secondsLeft) / totalSeconds) * 100
  const isUrgent = secondsLeft < 30 && secondsLeft > 0

  return (
    <div className="flex items-center gap-8 max-w-5xl mx-auto">
      {/* Left: activity + tips */}
      <div className="flex-1 space-y-3">
        <div className="bg-white rounded-xl p-5 shadow-md border-2 border-gray-100">
          <p className="text-lg text-gray-800 font-bold">{config.activity}</p>
          {config.activity_en && <p className="text-gray-500 italic mt-1">{config.activity_en}</p>}
        </div>
        {config.tips?.length > 0 && (
          <div className="bg-emerald-50 rounded-xl p-3 border-2 border-emerald-200">
            <p className="text-xs font-bold text-emerald-700 mb-1">💡 Tips:</p>
            <div className="space-y-1">
              {config.tips.map((tip: string, i: number) => (
                <p key={i} className="text-emerald-800 bg-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm">{tip}</p>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Right: timer */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className="relative w-44 h-44">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="6" />
            <circle cx="50" cy="50" r="45" fill="none" stroke={finished ? '#ef4444' : isUrgent ? '#f59e0b' : '#10b981'} strokeWidth="6" strokeLinecap="round" strokeDasharray={`${(1 - progress / 100) * 283} 283`} className="transition-all duration-1000" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <p className={`text-4xl font-black font-mono ${finished ? 'text-red-500 animate-pulse' : isUrgent ? 'text-amber-500' : 'text-gray-800'}`}>
              {String(min).padStart(2, '0')}:{String(sec).padStart(2, '0')}
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          {!running ? (
            <button onClick={start} className="px-5 py-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-xl font-black shadow-lg hover:scale-105 transition-all">▶ {secondsLeft === totalSeconds ? 'Iniciar' : 'Continuar'}</button>
          ) : (
            <button onClick={pause} className="px-5 py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-black shadow-lg hover:scale-105 transition-all">⏸ Pausar</button>
          )}
          <button onClick={reset} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-all">🔄</button>
        </div>
        {finished && <p className="text-xl font-black text-red-500 mt-2">⏰ ¡Tiempo!</p>}
      </div>
    </div>
  )
}

// ============================================
// 13. FREE HTML
// ============================================
function FreeHtml({ config }: { config: any }) {
  return <div className="theory-content max-w-5xl mx-auto bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100" dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.html || '') }} />
}

// ============================================
// 14. VIDEO EMBED
// ============================================
function VideoEmbed({ config }: { config: any }) {
  let embedUrl = config.url || ''
  const ytMatch = embedUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
  if (ytMatch) { embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`; if (config.start_seconds) embedUrl += `?start=${config.start_seconds}`; if (config.end_seconds) embedUrl += `${config.start_seconds ? '&' : '?'}end=${config.end_seconds}` }
  const driveMatch = embedUrl.match(/drive\.google\.com\/file\/d\/([^/]+)/)
  if (driveMatch) embedUrl = `https://drive.google.com/file/d/${driveMatch[1]}/preview`

  return (
    <div className="flex gap-5 max-w-5xl mx-auto items-start">
      <div className="flex-1 rounded-2xl overflow-hidden shadow-2xl border-2 border-gray-100">
        {config.title && <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-2 text-center"><p className="text-white font-bold text-sm">🎬 {config.title}</p></div>}
        <iframe src={embedUrl} title={config.title || 'Video embebido'} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen />
      </div>
      {config.discussion_questions?.length > 0 && (
        <div className="w-72 flex-shrink-0 bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
          <p className="font-bold text-purple-700 mb-2 text-sm">🗣 Después del video:</p>
          <div className="space-y-1.5">
            {config.discussion_questions.map((q: string, i: number) => (
              <p key={i} className="text-gray-700 bg-white px-3 py-2 rounded-lg shadow-sm text-sm"><span className="text-purple-500 font-bold">{i + 1}.</span> {q}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// 15. AUDIO LISTEN
// ============================================
function AudioListen({ config }: { config: any }) {
  const [showTranscript, setShowTranscript] = useState(config.show_transcript ?? false)
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {config.title && <div className="text-center"><span className="inline-block px-4 py-1.5 bg-gradient-to-r from-sky-400 to-blue-500 text-white rounded-full font-bold shadow-md text-sm">🎧 {config.title}</span></div>}
      {config.audio_url && <div className="bg-white rounded-xl p-4 border-2 border-gray-100 shadow-md flex justify-center"><audio controls src={config.audio_url} className="w-full max-w-md">
        <track kind="captions" srcLang="es" label="Español" />
      </audio></div>}
      <div className="flex justify-center">
        <button onClick={() => setShowTranscript(!showTranscript)} className="px-4 py-1.5 bg-sky-100 text-sky-700 rounded-full font-bold text-xs hover:bg-sky-200">{showTranscript ? '🙈 Ocultar' : '📝 Transcripción'}</button>
      </div>
      {showTranscript && (
        <div className="bg-sky-50 rounded-xl p-4 border-2 border-sky-200 animate-fadeIn">
          {config.transcript_en && <p className="text-gray-800 font-medium">{config.transcript_en}</p>}
          {config.transcript_es && <p className="text-gray-500 mt-1 text-sm">{config.transcript_es}</p>}
        </div>
      )}
      {config.questions?.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <p className="font-bold text-blue-700 mb-2 text-sm">🎯 Comprensión:</p>
          <div className="space-y-1">{config.questions.map((q: string, i: number) => (<p key={i} className="text-gray-700 bg-white px-3 py-2 rounded-lg shadow-sm text-sm"><span className="font-bold text-blue-500">{i + 1}.</span> {q}</p>))}</div>
        </div>
      )}
    </div>
  )
}

// ============================================
// 16. SENTENCE BUILDER
// ============================================
function SentenceBuilder({ config }: { config: any }) {
  const sentences = config.sentences || []
  const cols = sentences.length > 3 ? 'grid grid-cols-2 gap-3' : 'space-y-3'
  return <div className={`${cols} max-w-4xl mx-auto`}>{sentences.map((s: any, si: number) => <SentenceBuilderItem key={si} sentence={s} showTranslation={config.show_translation} index={si} />)}</div>
}

function SentenceBuilderItem({ sentence, showTranslation, index }: { sentence: any; showTranslation?: boolean; index: number }) {
  const [arranged, setArranged] = useState<string[]>([])
  const [available, setAvailable] = useState<string[]>([])
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)

  useEffect(() => { setAvailable([...sentence.words].sort(() => secureRandom() - 0.5)); setArranged([]); setIsCorrect(null) }, [sentence])
  const addWord = (word: string, idx: number) => { setArranged(prev => [...prev, word]); setAvailable(prev => prev.filter((_, i) => i !== idx)); setIsCorrect(null) }
  const removeWord = (idx: number) => { setArranged(prev => { const w = prev[idx]; setAvailable(a => [...a, w]); return prev.filter((_, i) => i !== idx) }); setIsCorrect(null) }
  const check = () => setIsCorrect(JSON.stringify(arranged) === JSON.stringify(sentence.correct_order))
  const colors = ['bg-blue-500', 'bg-rose-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500']

  return (
    <div className={`bg-white rounded-xl p-4 shadow-md border-2 transition-all ${isCorrect === true ? 'border-green-400 bg-green-50/50' : isCorrect === false ? 'border-red-300' : 'border-gray-100'}`}>
      {showTranslation && sentence.translation && <p className="text-gray-500 text-sm mb-2">🇪🇸 {sentence.translation}</p>}
      <div className={`min-h-[40px] flex flex-wrap gap-1.5 p-2.5 rounded-lg border-2 border-dashed mb-2 ${isCorrect === true ? 'border-green-400 bg-green-50' : isCorrect === false ? 'border-red-400 bg-red-50' : arranged.length > 0 ? 'border-blue-300 bg-blue-50/50' : 'border-gray-300 bg-gray-50'}`}>
        {arranged.length === 0 && <p className="text-gray-400 text-xs m-auto">Toca las palabras 👇</p>}
        {arranged.map((word, i) => <button key={i} onClick={() => removeWord(i)} className={`px-3 py-1 ${colors[index % colors.length]} text-white rounded-lg font-bold shadow text-sm`}>{word}</button>)}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {available.map((word, i) => <button key={i} onClick={() => addWord(word, i)} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 text-sm border border-gray-200">{word}</button>)}
      </div>
      {arranged.length === sentence.correct_order.length && isCorrect === null && <button onClick={check} className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold text-sm shadow hover:scale-105 transition-all">✓ Verificar</button>}
      {isCorrect === true && <p className="text-green-600 font-black text-sm">🎉 Très bien !</p>}
      {isCorrect === false && <p className="text-red-500 font-bold text-xs">❌ Respuesta: {sentence.correct_order.join(' ')}</p>}
    </div>
  )
}

// ============================================
// 17. CONVERSATION PROMPTS
// ============================================
function ConversationPrompts({ config }: { config: any }) {
  const prompts = config.prompts || []
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)
  const [order, setOrder] = useState<number[]>([])

  useEffect(() => {
    const indices = prompts.map((_: any, i: number) => i)
    setOrder(config.mode === 'random' ? indices.sort(() => secureRandom() - 0.5) : indices)
    setCurrentIndex(0); setShowAnswer(false)
  }, [config])

  const current = prompts[order[currentIndex]]
  if (!current) return null
  const next = () => { setShowAnswer(false); setCurrentIndex(prev => Math.min(prev + 1, prompts.length - 1)) }
  const prev = () => { setShowAnswer(false); setCurrentIndex(prev => Math.max(prev - 1, 0)) }
  const diffConfig: Record<string, { emoji: string; color: string; label: string }> = { easy: { emoji: '🟢', color: 'bg-green-100 text-green-700', label: 'Fácil' }, medium: { emoji: '🟡', color: 'bg-amber-100 text-amber-700', label: 'Intermedio' }, hard: { emoji: '🔴', color: 'bg-red-100 text-red-700', label: 'Difícil' } }
  const diff = diffConfig[current.difficulty]

  return (
    <div className="max-w-3xl mx-auto text-center space-y-4">
      <div className="flex justify-center gap-2 text-xs">
        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-bold">{config.pairs_or_group === 'group' ? '👥 En grupo' : '👫 En parejas'}</span>
        {config.time_per_prompt_seconds && <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-bold">⏱ {config.time_per_prompt_seconds}s</span>}
      </div>
      <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-gray-100">
        {diff && <span className={`inline-block px-3 py-0.5 rounded-full text-xs font-bold ${diff.color} mb-2`}>{diff.emoji} {diff.label}</span>}
        <p className="text-3xl font-black text-gray-800 leading-tight">{current.question_en}</p>
        <p className="text-lg text-gray-500 mt-2">{current.question_es}</p>
        {showAnswer && current.model_answer && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-4 mt-4 animate-fadeIn">
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1">Respuesta modelo</p>
            <p className="text-xl font-bold text-emerald-700">{current.model_answer}</p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-center gap-3">
        <button onClick={prev} disabled={currentIndex === 0} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 disabled:opacity-30">← Anterior</button>
        <button onClick={() => setShowAnswer(!showAnswer)} className="px-5 py-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-xl font-bold shadow-lg hover:scale-105 transition-all">{showAnswer ? '🙈 Ocultar' : '💡 Ver modelo'}</button>
        <button onClick={next} disabled={currentIndex === prompts.length - 1} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 disabled:opacity-30">Siguiente →</button>
      </div>
      <p className="text-gray-400 font-bold text-sm">{currentIndex + 1} / {prompts.length}</p>
    </div>
  )
}
